import { useMemo, useRef } from 'react';
import type {
  BalancePolicy,
  DeltaResolution,
  DeltaResolutionRule,
  PolicySide,
  StandardCalc,
} from '@capture/shared';
import { useFormConfigStore } from '../state/store';
import { useRemittanceIssues } from './useRemittanceIssues';
import { buildPickerData } from './remittance-picker-data';
import { ExpressionEditor, DEFAULT_PICKER_DATA } from '../shared/ExpressionEditor';
import './BalancePolicySection.css';

type Side = 'shortPay' | 'overPay';

const MODE_OPTIONS = ['off', 'accept', 'flag', 'block'] as const;
type ModeOption = (typeof MODE_OPTIONS)[number];

const CALC_OPTIONS: StandardCalc[] = ['largest-remainder', 'pro-rata-allocations'];

const SIDE_META: Record<Side, { title: string; boundLabel: string }> = {
  shortPay: { title: 'Short-pay', boundLabel: 'Max shortfall accepted (cents)' },
  overPay: { title: 'Overpay', boundLabel: 'Max excess accepted (cents)' },
};

/** True when a `DeltaResolution` has nothing configured — no rules, no
 *  calculation fallback, no default target. Used both to decide whether to
 *  drop the key entirely on write, and (shortPay only) to show the implicit
 *  default hint. */
function isEmptyDeltaResolution(dr: DeltaResolution | undefined): boolean {
  return !dr || ((dr.rules?.length ?? 0) === 0 && dr.calculation === undefined && dr.defaultTargetId === undefined);
}

/**
 * SP6 Task 6 — Balance Policy section of the Remittance job-config tab.
 * Renders the short-pay / overpay cards (mode, bound, delta-resolution
 * chain) that make up `remittance.balancePolicy`. Every write recomputes
 * and sends the FULL `BalancePolicy` object via `updateRemittance` — the
 * store's merge is shallow, so a partial patch would silently drop the
 * other side. Switching a side to 'off' deletes that side's key entirely
 * (mode:'off' is not a representable state); when both sides end up off,
 * `balancePolicy` itself is removed from the draft.
 */
export function BalancePolicySection() {
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const updateRemittance = useFormConfigStore((s) => s.updateRemittance);
  const issues = useRemittanceIssues();

  const balancePolicy = contentV3?.remittance?.balancePolicy;
  const targets = contentV3?.remittance?.allocation?.targets ?? [];
  const pickerData = useMemo(() => (contentV3 ? buildPickerData(contentV3) : DEFAULT_PICKER_DATA), [contentV3]);

  // Stable CLIENT-SIDE React keys for rule rows, per side. ExpressionEditor
  // holds uncommitted local state (builder model, raw/builder mode) that only
  // syncs to `value` on commit — keying rows by index would leave an editor
  // mounted at its old position after a reorder, feed it the OTHER row's
  // value, and let the resync effect discard the in-progress edit. These ids
  // reorder/splice in lockstep with the rules array, mutated in the same
  // handlers that write the store (synchronously, before the re-render), and
  // are never persisted — the stored `DeltaResolutionRule` shape is unchanged.
  const ruleIdCounter = useRef(0);
  const ruleRowIdsRef = useRef<Record<Side, string[]>>({ shortPay: [], overPay: [] });

  /** The side's row-id list, lazily grown/trimmed to `count` so it self-heals
   *  against rules changes this component didn't make (seeding, store reset).
   *  Growth appends — which also covers addRule, since new rules append. */
  function ruleRowIds(side: Side, count: number): string[] {
    const ids = ruleRowIdsRef.current[side];
    while (ids.length < count) ids.push(`rule-row-${ruleIdCounter.current++}`);
    if (ids.length > count) ids.length = count;
    return ids;
  }

  function writeSide(side: Side, nextSide: PolicySide | undefined) {
    const next: BalancePolicy = { ...balancePolicy };
    if (nextSide) next[side] = nextSide;
    else delete next[side];
    const hasAny = next.shortPay !== undefined || next.overPay !== undefined;
    updateRemittance({ balancePolicy: hasAny ? next : undefined });
  }

  function handleModeChange(side: Side, mode: ModeOption) {
    if (mode === 'off') {
      writeSide(side, undefined);
      return;
    }
    const current = balancePolicy?.[side];
    writeSide(side, { ...current, mode });
  }

  function handleBoundCentsChange(side: Side, raw: string) {
    const current = balancePolicy?.[side];
    if (!current) return;
    if (raw === '') {
      writeSide(side, { ...current, boundCents: undefined });
      return;
    }
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    writeSide(side, { ...current, boundCents: n });
  }

  function writeDeltaResolution(side: Side, nextDR: DeltaResolution) {
    const current = balancePolicy?.[side];
    if (!current) return;
    const nextSide: PolicySide = { ...current };
    if (isEmptyDeltaResolution(nextDR)) delete nextSide.deltaResolution;
    else nextSide.deltaResolution = nextDR;
    writeSide(side, nextSide);
  }

  function addRule(side: Side) {
    const current = balancePolicy?.[side];
    if (!current) return;
    const dr = current.deltaResolution ?? {};
    const rules = dr.rules ?? [];
    const newRule: DeltaResolutionRule = { when: '', targetId: '' };
    writeDeltaResolution(side, { ...dr, rules: [...rules, newRule] });
  }

  function removeRule(side: Side, index: number) {
    const current = balancePolicy?.[side];
    const dr = current?.deltaResolution;
    if (!dr) return;
    ruleRowIdsRef.current[side].splice(index, 1);
    const rules = (dr.rules ?? []).filter((_, i) => i !== index);
    writeDeltaResolution(side, { ...dr, rules });
  }

  function moveRule(side: Side, index: number, direction: -1 | 1) {
    const current = balancePolicy?.[side];
    const dr = current?.deltaResolution;
    if (!dr?.rules) return;
    const target = index + direction;
    if (target < 0 || target >= dr.rules.length) return;
    const ids = ruleRowIdsRef.current[side];
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    const rules = [...dr.rules];
    [rules[index], rules[target]] = [rules[target]!, rules[index]!];
    writeDeltaResolution(side, { ...dr, rules });
  }

  function updateRuleWhen(side: Side, index: number, when: string) {
    const current = balancePolicy?.[side];
    const dr = current?.deltaResolution;
    if (!dr?.rules) return;
    const rules = dr.rules.map((r, i) => (i === index ? { ...r, when } : r));
    writeDeltaResolution(side, { ...dr, rules });
  }

  function updateRuleTarget(side: Side, index: number, targetId: string) {
    const current = balancePolicy?.[side];
    const dr = current?.deltaResolution;
    if (!dr?.rules) return;
    const rules = dr.rules.map((r, i) => (i === index ? { when: r.when, targetId } : r));
    writeDeltaResolution(side, { ...dr, rules });
  }

  function updateRuleCalculation(side: Side, index: number, calculation: StandardCalc) {
    const current = balancePolicy?.[side];
    const dr = current?.deltaResolution;
    if (!dr?.rules) return;
    const rules = dr.rules.map((r, i) => (i === index ? { when: r.when, calculation } : r));
    writeDeltaResolution(side, { ...dr, rules });
  }

  function setRuleOutcome(side: Side, index: number, outcome: 'target' | 'calculation') {
    const current = balancePolicy?.[side];
    const dr = current?.deltaResolution;
    if (!dr?.rules) return;
    if (outcome === 'target') updateRuleTarget(side, index, '');
    else updateRuleCalculation(side, index, CALC_OPTIONS[0]!);
  }

  function updateCalculationFallback(side: Side, raw: string) {
    const current = balancePolicy?.[side];
    const dr = current?.deltaResolution ?? {};
    writeDeltaResolution(side, { ...dr, calculation: raw === 'none' ? undefined : (raw as StandardCalc) });
  }

  function updateDefaultTarget(side: Side, raw: string) {
    const current = balancePolicy?.[side];
    const dr = current?.deltaResolution ?? {};
    writeDeltaResolution(side, { ...dr, defaultTargetId: raw === 'none' ? undefined : raw });
  }

  return (
    <section className="remittance-balance-policy">
      <h3 className="remittance-balance-policy__heading">Balance policy</h3>

      {issues.policy.length > 0 && (
        <ul className="remittance-balance-policy__issues" role="alert">
          {issues.policy.map((issue, i) => (
            <li key={`${issue.code}-${i}`}>{issue.message}</li>
          ))}
        </ul>
      )}

      <div className="remittance-balance-policy__cards">
        {(['shortPay', 'overPay'] as const).map((side) => (
          <PolicyCard
            key={side}
            side={side}
            policySide={balancePolicy?.[side]}
            ruleRowIds={[...ruleRowIds(side, balancePolicy?.[side]?.deltaResolution?.rules?.length ?? 0)]}
            targets={targets}
            pickerData={pickerData}
            onModeChange={(mode) => handleModeChange(side, mode)}
            onBoundCentsChange={(raw) => handleBoundCentsChange(side, raw)}
            onAddRule={() => addRule(side)}
            onRemoveRule={(index) => removeRule(side, index)}
            onMoveRule={(index, direction) => moveRule(side, index, direction)}
            onRuleWhenChange={(index, when) => updateRuleWhen(side, index, when)}
            onRuleTargetChange={(index, targetId) => updateRuleTarget(side, index, targetId)}
            onRuleCalculationChange={(index, calc) => updateRuleCalculation(side, index, calc)}
            onRuleOutcomeChange={(index, outcome) => setRuleOutcome(side, index, outcome)}
            onCalculationFallbackChange={(raw) => updateCalculationFallback(side, raw)}
            onDefaultTargetChange={(raw) => updateDefaultTarget(side, raw)}
          />
        ))}
      </div>
    </section>
  );
}

interface PolicyCardProps {
  side: Side;
  policySide: PolicySide | undefined;
  /** Stable client-side React key per rule row, parallel to
   *  `policySide.deltaResolution.rules` — see BalancePolicySection. */
  ruleRowIds: string[];
  targets: { id: string; label: string }[];
  pickerData: ReturnType<typeof buildPickerData>;
  onModeChange: (mode: ModeOption) => void;
  onBoundCentsChange: (raw: string) => void;
  onAddRule: () => void;
  onRemoveRule: (index: number) => void;
  onMoveRule: (index: number, direction: -1 | 1) => void;
  onRuleWhenChange: (index: number, when: string) => void;
  onRuleTargetChange: (index: number, targetId: string) => void;
  onRuleCalculationChange: (index: number, calc: StandardCalc) => void;
  onRuleOutcomeChange: (index: number, outcome: 'target' | 'calculation') => void;
  onCalculationFallbackChange: (raw: string) => void;
  onDefaultTargetChange: (raw: string) => void;
}

function PolicyCard({
  side,
  policySide,
  ruleRowIds,
  targets,
  pickerData,
  onModeChange,
  onBoundCentsChange,
  onAddRule,
  onRemoveRule,
  onMoveRule,
  onRuleWhenChange,
  onRuleTargetChange,
  onRuleCalculationChange,
  onRuleOutcomeChange,
  onCalculationFallbackChange,
  onDefaultTargetChange,
}: PolicyCardProps) {
  const meta = SIDE_META[side];
  const mode: ModeOption = policySide?.mode ?? 'off';
  const dr = policySide?.deltaResolution;
  const rules = dr?.rules ?? [];
  const showImplicitDefaultHint = side === 'shortPay' && isEmptyDeltaResolution(dr);

  return (
    <div className="remittance-balance-policy__card" data-side={side}>
      <h4 className="remittance-balance-policy__card-heading">{meta.title}</h4>

      <label className="remittance-balance-policy__field">
        Mode
        <select
          aria-label={`${meta.title} mode`}
          value={mode}
          onChange={(e) => onModeChange(e.target.value as ModeOption)}
        >
          {MODE_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </label>

      {policySide && (
        <>
          <label className="remittance-balance-policy__field">
            {meta.boundLabel}
            <input
              type="number"
              aria-label={meta.boundLabel}
              value={policySide.boundCents ?? ''}
              onChange={(e) => onBoundCentsChange(e.target.value)}
            />
          </label>

          {mode === 'accept' && (
            <div className="remittance-balance-policy__chain">
              <h5 className="remittance-balance-policy__chain-heading">Delta resolution</h5>

              {showImplicitDefaultHint && (
                <p className="remittance-balance-policy__hint">Default: largest-remainder across coupons</p>
              )}

              <ol className="remittance-balance-policy__rules">
                {rules.map((rule, index) => {
                  const outcome: 'target' | 'calculation' = rule.calculation !== undefined ? 'calculation' : 'target';
                  return (
                    <li key={ruleRowIds[index]} className="remittance-balance-policy__rule">
                      <ExpressionEditor
                        label={`Rule ${index + 1} when`}
                        value={rule.when}
                        onChange={(when) => onRuleWhenChange(index, when)}
                        pickerData={pickerData}
                      />

                      {side === 'shortPay' ? (
                        <div
                          className="remittance-balance-policy__outcome"
                          role="radiogroup"
                          aria-label={`Rule ${index + 1} outcome`}
                        >
                          <label>
                            <input
                              type="radio"
                              name={`rule-outcome-${side}-${index}`}
                              checked={outcome === 'target'}
                              onChange={() => onRuleOutcomeChange(index, 'target')}
                            />
                            Post to target
                          </label>
                          <label>
                            <input
                              type="radio"
                              name={`rule-outcome-${side}-${index}`}
                              checked={outcome === 'calculation'}
                              onChange={() => onRuleOutcomeChange(index, 'calculation')}
                            />
                            Apply calculation
                          </label>
                        </div>
                      ) : (
                        <p className="remittance-balance-policy__outcome-label">Post to target</p>
                      )}

                      {outcome === 'target' ? (
                        <label className="sr-only">
                          {`Rule ${index + 1} target`}
                          <select
                            aria-label={`Rule ${index + 1} target`}
                            value={rule.targetId ?? ''}
                            onChange={(e) => onRuleTargetChange(index, e.target.value)}
                          >
                            <option value="" disabled>
                              Select a target
                            </option>
                            {targets.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.label || t.id}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : (
                        <label className="sr-only">
                          {`Rule ${index + 1} calculation`}
                          <select
                            aria-label={`Rule ${index + 1} calculation`}
                            value={rule.calculation}
                            onChange={(e) => onRuleCalculationChange(index, e.target.value as StandardCalc)}
                          >
                            {CALC_OPTIONS.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}

                      <div className="remittance-balance-policy__rule-actions">
                        <button
                          type="button"
                          onClick={() => onMoveRule(index, -1)}
                          disabled={index === 0}
                          aria-label={`Move rule ${index + 1} up`}
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          onClick={() => onMoveRule(index, 1)}
                          disabled={index === rules.length - 1}
                          aria-label={`Move rule ${index + 1} down`}
                        >
                          Down
                        </button>
                        <button
                          type="button"
                          onClick={() => onRemoveRule(index)}
                          aria-label={`Remove rule ${index + 1}`}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ol>

              <button type="button" className="remittance-balance-policy__add-rule" onClick={onAddRule}>
                Add rule
              </button>
              <p className="remittance-balance-policy__caption">first matching rule wins</p>

              {side === 'shortPay' && (
                <label className="remittance-balance-policy__field">
                  Calculation fallback
                  <select
                    aria-label={`${meta.title} calculation fallback`}
                    value={dr?.calculation ?? 'none'}
                    onChange={(e) => onCalculationFallbackChange(e.target.value)}
                  >
                    <option value="none">None</option>
                    {CALC_OPTIONS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="remittance-balance-policy__field">
                Default target
                <select
                  aria-label={`${meta.title} default target`}
                  value={dr?.defaultTargetId ?? 'none'}
                  onChange={(e) => onDefaultTargetChange(e.target.value)}
                >
                  <option value="none">None</option>
                  {targets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label || t.id}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </>
      )}
    </div>
  );
}
